import DigitalBookerApiClient from '../../utils/digital-booker-api-client';
import uniqBy from 'lodash/uniqBy';
import flattenDeep from 'lodash/flattenDeep';
import find from 'lodash/find';
import moment from 'moment';

class BookingDataLoader {

    constructor(systemName, language, apiClient) {
        moment.locale(language);
        this.systemName = systemName;
        this.language = language;
        this.apiClient = apiClient ? apiClient : new DigitalBookerApiClient(this.systemName, this.language);
        this.loadViews = this.loadViews.bind(this);
        this.loadLocations = this.loadLocations.bind(this);
        this.loadCategories = this.loadCategories.bind(this);
        this.loadServices = this.loadServices.bind(this);
        this.loadResources = this.loadResources.bind(this);
        this.loadAllData = this.loadAllData.bind(this);
        this.loadAvailableTimes = this.loadAvailableTimes.bind(this);
        this.doLoadAvailableTimes = this.doLoadAvailableTimes.bind(this);
        this.loadExtraFields = this.loadExtraFields.bind(this);
        this.currencySymbol = new Map([['fi', '€'], ['se', 'SEK'], ['no', 'NOK']]).get(this.language);
    }

    loadAllData(newState) {
        return this.loadViews(newState)
            .then(this.loadLocations)
            .then(this.loadCategories)
            .then(this.loadServices)
            .then(this.loadResources)
            .then(this.loadAvailableTimes)
            .then(this.loadExtraFields);
    }

    loadExtraFields(newState) {
        if(newState.selections.location && newState.selections.view) {
            this.apiClient.loadFields(newState.selections.location.url_text, newState.selections.view.url_text)
                .then(fields => {
                    newState.extraFields = fields;
                    return newState;
                });
        }
        newState.extraFields = [];
        return Promise.resolve(newState);
    }

    allowedValueOrEmpty(allowedValues, fieldName, currentValue) {
        if(allowedValues !== undefined && currentValue !== undefined && currentValue[fieldName] !== undefined) {
            return find(allowedValues, (element)  => element[fieldName] === currentValue[fieldName]) !== undefined ? currentValue : undefined;
        }
        return undefined;
    }

    loadViews(newState) {
        return this.apiClient.loadStores().then(stores => {
            newState.views = uniqBy(flattenDeep(stores.map(store => store.views)), 'id');
            newState.selections.view = this.allowedValueOrEmpty(newState.views, 'url_text', newState.selections.view);
            return newState;
        }).then(newState => {
            if(newState.views.length === 1 && newState.selections.view === undefined) {
                newState.selections.view = newState.views[0];
            }
            return newState;
        })
    }

    loadLocations(newState) {
        if(newState.selections.view) {
            return this.apiClient.loadStores().then(stores => {
                newState.locations = stores.filter(store => store.views.map(view => view.id === newState.selections.view.id));
                newState.selections.location = this.allowedValueOrEmpty(newState.locations, 'url_text', newState.selections.location);
                return newState;
            });
        }
        newState.locations = [];
        newState.selections.location = undefined;
        return Promise.resolve(newState);
    }

    loadCategories(newState) {
        if(newState.selections.location) {
            return this.apiClient.loadServiceCategories(newState.selections.location.url_text, newState.selections.view.url_text)
                .then(categories => {
                    newState.categories = categories;
                    newState.selections.category = this.allowedValueOrEmpty(categories, 'id', newState.selections.category);
                    return newState;
                });
        }
        newState.categories = [];
        newState.selections.category = undefined;
        return Promise.resolve(newState);
    }

    loadServices(newState) {
        if(newState.selections.location && newState.selections.category) {
            return this.apiClient
                .loadServices(newState.selections.location.url_text, newState.selections.view.url_text, newState.selections.category.id)
                .then(services => {
                    newState.services = services.map(service => {
                        service.name = service.name.indexOf("(") > -1 ? service.name.substring(0, service.name.indexOf("(")) : service.name; //Pesu (Akita) => Pesu
                        return service;
                    });
                    newState.selections.service = this.allowedValueOrEmpty(services, 'id', newState.selections.service);
                    if(newState.selections.extra_services && newState.selections.service && newState.selections.service.extra_services) {
                        const allowedExtraServices = newState.selections.service.extra_services.map(es => es.id.toString());
                        newState.selections.extra_services = newState.selections.extra_services.filter(selectedEs => allowedExtraServices.indexOf(selectedEs)>-1);
                    }
                    return newState;
                });
        }
        newState.services = [];
        newState.selections.service = undefined;
        newState.selections.extra_services = [];
        return Promise.resolve(newState);
    }

    loadResources(newState) {
        if(newState.selections.service && newState.selections.location && newState.selections.view && !newState.selections.service.phone_only) {
            return this.apiClient.loadResources(newState.selections.location.url_text, newState.selections.view.url_text, newState.selections.service.id, newState.selections.extra_services)
                .then(resources => {
                    newState.resources = resources;
                    newState.selections.resource = this.allowedValueOrEmpty(resources, 'id', newState.selections.resource);
                    return newState;
                });
        }
        newState.resources = [];
        newState.selections.resource = undefined;
        return Promise.resolve(newState);
    }

    doLoadAvailableTimes(fromDate, selections) {
        if(fromDate !== undefined) {
            fromDate = moment(fromDate).format('YYYY-MM-DD');
        }
        return this.apiClient.loadAvailableTimes(selections.location.url_text, selections.view.url_text, selections.service.id, selections.resource.id, selections.extra_services, fromDate)
    }

    loadAvailableTimes(newState) {
        const currencySymbol = this.currencySymbol;
        function toEvents(day) {
            return day.available.map(time => {
                return {
                    title: time.price + currencySymbol,
                    price: time.price,
                    resources: time.resources,
                    date:  day.date,
                    from: time.from,
                    to: time.to,
                    start: moment(day.date+" "+time.from).toDate(),
                    end: moment(day.date+" "+time.to).toDate()
                };
            });
        }
        if(newState.selections.service && newState.selections.location && newState.selections.view && newState.selections.resource) {
            return this.doLoadAvailableTimes(newState.selections.selectedDate, newState.selections)
                .then(response => {
                    if(response.next_available_date) {
                        return this.doLoadAvailableTimes(response.next_available_date, newState.selections).then(response => response.data);
                    } else {
                        return response.data;
                    }
                })
                .then(dates => dates.filter(date => Array.isArray(date.available)))
                .then(availableTimes => flattenDeep(availableTimes.map(toEvents)))
                .then(availableEvents => {
                    newState.availableTimes = availableEvents;
                    return newState;
                });
        }
        return Promise.resolve(newState);
    }


}

export {BookingDataLoader as default}


// WEBPACK FOOTER //
// ../components/booking/BookingDataLoader.js